
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { supabase } from '@/integrations/supabase/client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, Plus, Edit, Trash2, ExternalLink, Settings, ChevronDown, Search } from 'lucide-react';
import { MappingEndpointsSection } from './MappingEndpointsSection';
import { BotMappingCsvImport } from './BotMappingCsvImport';
import { BulkAddMappingDialog } from './BulkAddMappingDialog';
import { ParentBotCardsContainer } from './ParentBotCardsContainer';
import { Checkbox } from '@/components/ui/checkbox';

interface Bot {
  id: string;
  name: string;
  signal_token: string;
  type: string;
}

interface BotMapping {
  id: string;
  parent_bot_id: string;
  child_bot_id: string;
  is_active: boolean;
  notes: string | null;
  instrument: string | null;
  tp_usd: number | null;
  sl_usd: number | null;
  quantity: number | null;
  created_at: string;
  updated_at: string;
  parent_bot: Bot | null;
  child_bot: Bot | null;
}

export const BotMappingTab: React.FC = () => {
  const [selectedParentBot, setSelectedParentBot] = useState<string>('');
  const [selectedChildBot, setSelectedChildBot] = useState<string>('');
  const [parentBotType, setParentBotType] = useState<string>('all');
  const [childBotType, setChildBotType] = useState<string>('all');
  const [parentSearchQuery, setParentSearchQuery] = useState('');
  const [childSearchQuery, setChildSearchQuery] = useState('');
  const [notes, setNotes] = useState('');
  const [instrument, setInstrument] = useState('');
  const [tpUsd, setTpUsd] = useState('');
  const [slUsd, setSlUsd] = useState('');
  const [quantity, setQuantity] = useState('');
  const [addDefaultEndpoint, setAddDefaultEndpoint] = useState(true);
  const [isBulkAddOpen, setIsBulkAddOpen] = useState(false);
  const [editingMapping, setEditingMapping] = useState<BotMapping | null>(null);
  const [expandedMappings, setExpandedMappings] = useState<Set<string>>(new Set());
  const queryClient = useQueryClient();

  // Fetch all bots
  const { data: bots = [], isLoading: botsLoading } = useQuery({
    queryKey: ['bots-for-merge'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('bots')
        .select('id, name, signal_token, type')
        .order('name');
      
      if (error) throw error;
      return data as Bot[];
    }
  });

  // Fetch bot mappings
  const { data: mappings = [], isLoading: mappingsLoading } = useQuery({
    queryKey: ['bot-mappings'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('bot_merge_mapping')
        .select(`
          *,
          parent_bot:bots!parent_bot_id(id, name, signal_token, type),
          child_bot:bots!child_bot_id(id, name, signal_token, type)
        `)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      // Filter out mappings where parent_bot or child_bot is null
      const validMappings = (data || []).filter(mapping => {
        if (!mapping.parent_bot || !mapping.child_bot) {
          console.warn('Found mapping with missing bot data:', mapping);
          return false;
        }
        return true;
      });
      
      return validMappings as BotMapping[];
    }
  });

  // Create mapping mutation
  const createMappingMutation = useMutation({
    mutationFn: async (data: { 
      parent_bot_id: string; 
      child_bot_id: string; 
      notes?: string; 
      instrument?: string;
      tp_usd?: number;
      sl_usd?: number;
      quantity?: number;
      add_default_endpoint?: boolean;
    }) => {
      const { data: mappingData, error } = await supabase
        .from('bot_merge_mapping')
        .insert({
          parent_bot_id: data.parent_bot_id,
          child_bot_id: data.child_bot_id,
          notes: data.notes || null,
          instrument: data.instrument || null,
          tp_usd: data.tp_usd || null,
          sl_usd: data.sl_usd || null,
          quantity: data.quantity || null,
          created_by: (await supabase.auth.getUser()).data.user?.id
        })
        .select()
        .single();
      
      if (error) throw error;

      // Add default endpoint if requested
      if (data.add_default_endpoint && mappingData) {
        const { error: endpointError } = await (supabase as any)
          .from('bot_merge_endpoints')
          .insert({
            mapping_id: mappingData.id,
            name: 'Signal Tracking',
            url: 'https://lbvhywxvthwgiebjsrlr.supabase.co/functions/v1/bot-signal-webhook',
            is_active: true
          });
        
        if (endpointError) throw endpointError;
      }
      
      return mappingData;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bot-mappings'] });
      setSelectedParentBot('');
      setSelectedChildBot('');
      setNotes('');
      setInstrument('');
      setTpUsd('');
      setSlUsd('');
      setQuantity('');
      toast.success('Bot mapping created successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to create bot mapping');
    }
  });

  // Update mapping mutation
  const updateMappingMutation = useMutation({
    mutationFn: async (data: { id: string; is_active?: boolean; notes?: string }) => {
      const { error } = await supabase
        .from('bot_merge_mapping')
        .update({
          is_active: data.is_active,
          notes: data.notes
        })
        .eq('id', data.id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bot-mappings'] });
      toast.success('Bot mapping updated successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update bot mapping');
    }
  });

  // Delete mapping mutation
  const deleteMappingMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('bot_merge_mapping')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bot-mappings'] });
      toast.success('Bot mapping deleted successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to delete bot mapping');
    }
  });

  const handleCreateMapping = () => {
    if (!selectedParentBot || !selectedChildBot) {
      toast.error('Please select both parent and child bots');
      return;
    }

    if (selectedParentBot === selectedChildBot) {
      toast.error('Parent and child bots cannot be the same');
      return;
    }

    // Check for exact duplicate mapping
    const duplicateMapping = mappings.find(mapping => 
      mapping.parent_bot_id === selectedParentBot && 
      mapping.child_bot_id === selectedChildBot
    );
    
    if (duplicateMapping) {
      toast.error('This exact mapping already exists');
      return;
    }

    // Show warning for multi-parent setup
    if (existingMappingsForChild.length > 0) {
      const childBot = bots.find(b => b.id === selectedChildBot);
      const parentBot = bots.find(b => b.id === selectedParentBot);
      const confirmed = confirm(
        `Warning: ${childBot?.name} is already mapped to ${existingMappingsForChild.length} parent bot(s). ` +
        `Adding ${parentBot?.name} will create multi-parent forwarding. Continue?`
      );
      if (!confirmed) return;
    }

    createMappingMutation.mutate({
      parent_bot_id: selectedParentBot,
      child_bot_id: selectedChildBot,
      notes,
      instrument: instrument.trim() || undefined,
      tp_usd: tpUsd ? parseFloat(tpUsd) : undefined,
      sl_usd: slUsd ? parseFloat(slUsd) : undefined,
      quantity: quantity ? parseFloat(quantity) : undefined,
      add_default_endpoint: addDefaultEndpoint
    });
  };

  const handleToggleActive = (mapping: BotMapping) => {
    updateMappingMutation.mutate({
      id: mapping.id,
      is_active: !mapping.is_active,
      notes: mapping.notes || undefined
    });
  };

  const handleDeleteMapping = (mapping: BotMapping) => {
    const parentName = mapping.parent_bot?.name || 'Unknown Parent';
    const childName = mapping.child_bot?.name || 'Unknown Child';
    
    if (confirm(`Are you sure you want to delete the mapping: ${childName} → ${parentName}?`)) {
      deleteMappingMutation.mutate(mapping.id);
    }
  };

  const toggleMappingExpanded = (mappingId: string) => {
    setExpandedMappings(prev => {
      const newSet = new Set(prev);
      if (newSet.has(mappingId)) {
        newSet.delete(mappingId);
      } else {
        newSet.add(mappingId);
      }
      return newSet;
    });
  };

  // Filter parent bots based on type and search query
  const availableParentBots = bots
    .filter(bot => bot.id !== selectedChildBot)
    .filter(bot => {
      // Filter by bot type
      if (parentBotType !== 'all' && bot.type !== parentBotType) {
        return false;
      }
      // Filter by search query
      if (!parentSearchQuery) return true;
      const searchLower = parentSearchQuery.toLowerCase();
      return (
        bot.name.toLowerCase().includes(searchLower) ||
        bot.type.toLowerCase().includes(searchLower) ||
        bot.signal_token.toLowerCase().includes(searchLower)
      );
    });

  // Filter child bots based on type and search query  
  const availableChildBots = bots
    .filter(bot => bot.id !== selectedParentBot)
    .filter(bot => {
      // Filter by bot type
      if (childBotType !== 'all' && bot.type !== childBotType) {
        return false;
      }
      // Filter by search query
      if (!childSearchQuery) return true;
      const searchLower = childSearchQuery.toLowerCase();
      return (
        bot.name.toLowerCase().includes(searchLower) ||
        bot.type.toLowerCase().includes(searchLower) ||
        bot.signal_token.toLowerCase().includes(searchLower)
      );
    });

  // Check if selected child bot already has mappings
  const existingMappingsForChild = mappings.filter(mapping => 
    mapping.child_bot_id === selectedChildBot && mapping.is_active
  );

  if (botsLoading || mappingsLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Create New Mapping */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Plus className="h-5 w-5" />
              Create Bot Mapping
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsBulkAddOpen(true)}
              >
                <Plus className="h-4 w-4 mr-2" />
                Bulk Add
              </Button>
              <BotMappingCsvImport onImportComplete={() => queryClient.invalidateQueries({ queryKey: ['bot-mappings'] })} />
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Parent Bot (Receiver)</Label>
              <Select value={parentBotType} onValueChange={setParentBotType}>
                <SelectTrigger>
                  <SelectValue placeholder="Select bot type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="user_bot">User Bot</SelectItem>
                  <SelectItem value="prop_bot">Prop Trading Bot</SelectItem>
                  <SelectItem value="shadow_bot">Shadow Bot</SelectItem>
                  <SelectItem value="premium_bot">Premium Bot</SelectItem>
                </SelectContent>
              </Select>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search parent bots..."
                  value={parentSearchQuery}
                  onChange={(e) => setParentSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Select value={selectedParentBot} onValueChange={setSelectedParentBot}>
                <SelectTrigger>
                  <SelectValue placeholder="Select parent bot" />
                </SelectTrigger>
                <SelectContent>
                  {availableParentBots.length === 0 ? (
                    <div className="p-2 text-sm text-muted-foreground text-center">
                      No bots found
                    </div>
                  ) : (
                    availableParentBots.map(bot => (
                      <SelectItem key={bot.id} value={bot.id}>
                        {bot.name} ({bot.type})
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Child Bot (Sender)</Label>
              <Select value={childBotType} onValueChange={setChildBotType}>
                <SelectTrigger>
                  <SelectValue placeholder="Select bot type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="user_bot">User Bot</SelectItem>
                  <SelectItem value="prop_bot">Prop Trading Bot</SelectItem>
                  <SelectItem value="shadow_bot">Shadow Bot</SelectItem>
                  <SelectItem value="premium_bot">Premium Bot</SelectItem>
                </SelectContent>
              </Select>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search child bots..."
                  value={childSearchQuery}
                  onChange={(e) => setChildSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Select value={selectedChildBot} onValueChange={setSelectedChildBot}>
                <SelectTrigger>
                  <SelectValue placeholder="Select child bot" />
                </SelectTrigger>
                <SelectContent>
                  {availableChildBots.length === 0 ? (
                    <div className="p-2 text-sm text-muted-foreground text-center">
                      No bots found
                    </div>
                  ) : (
                    availableChildBots.map(bot => {
                      const existingCount = mappings.filter(m => 
                        m.child_bot_id === bot.id && m.is_active
                      ).length;
                      return (
                        <SelectItem key={bot.id} value={bot.id}>
                          {bot.name} ({bot.type})
                          {existingCount > 0 && (
                            <Badge variant="secondary" className="ml-2 text-xs">
                              {existingCount} parent{existingCount > 1 ? 's' : ''}
                            </Badge>
                          )}
                        </SelectItem>
                      );
                    })
                  )}
                </SelectContent>
              </Select>
              {existingMappingsForChild.length > 0 && (
                <div className="text-xs text-amber-600 bg-amber-50 p-2 rounded border border-amber-200">
                  ⚠️ This child bot is already mapped to {existingMappingsForChild.length} parent bot(s). 
                  Signals will be forwarded to multiple parents.
                </div>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label>Notes (Optional)</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add notes about this mapping..."
              rows={2}
            />
          </div>

          {/* Trading Configuration */}
          <div className="space-y-3 p-4 bg-muted/50 rounded-lg border">
            <div className="flex items-center gap-2 mb-2">
              <Settings className="h-4 w-4" />
              <Label className="text-sm font-semibold">Trading Configuration (Optional)</Label>
            </div>
            <p className="text-xs text-muted-foreground">
              Configure TP/SL/Quantity for realtime trade mapping. If not set, will fallback to published analysis config.
            </p>
            
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="instrument" className="text-xs">Instrument</Label>
                <Input
                  id="instrument"
                  value={instrument}
                  onChange={(e) => setInstrument(e.target.value)}
                  placeholder="e.g., EURUSD, XAUUSD"
                  className="h-9"
                />
              </div>
              
              <div className="space-y-1.5">
                <Label htmlFor="quantity" className="text-xs">Quantity (base)</Label>
                <Input
                  id="quantity"
                  type="number"
                  step="0.01"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  placeholder="e.g., 0.01"
                  className="h-9"
                />
              </div>
              
              <div className="space-y-1.5">
                <Label htmlFor="tp-usd" className="text-xs">TP per trade ($)</Label>
                <Input
                  id="tp-usd"
                  type="number"
                  step="0.01"
                  value={tpUsd}
                  onChange={(e) => setTpUsd(e.target.value)}
                  placeholder="e.g., 20"
                  className="h-9"
                />
              </div>
              
              <div className="space-y-1.5">
                <Label htmlFor="sl-usd" className="text-xs">SL per trade ($)</Label>
                <Input
                  id="sl-usd"
                  type="number"
                  step="0.01"
                  value={slUsd}
                  onChange={(e) => setSlUsd(e.target.value)}
                  placeholder="e.g., 20"
                  className="h-9"
                />
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-2 p-3 bg-muted/50 rounded-lg">
            <Checkbox
              id="add-default-endpoint"
              checked={addDefaultEndpoint}
              onCheckedChange={(checked) => setAddDefaultEndpoint(checked as boolean)}
            />
            <Label htmlFor="add-default-endpoint" className="text-sm cursor-pointer flex-1">
              Add default "Signal Tracking" endpoint 
              <span className="text-xs text-muted-foreground block mt-1">
                URL: https://lbvhywxvthwgiebjsrlr.supabase.co/functions/v1/bot-signal-webhook
              </span>
            </Label>
          </div>

          <Button
            onClick={handleCreateMapping}
            disabled={createMappingMutation.isPending}
            className="w-full"
          >
            {createMappingMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Plus className="h-4 w-4 mr-2" />
            )}
            Create Mapping
          </Button>
        </CardContent>
      </Card>

      {/* Existing Mappings */}
      <ParentBotCardsContainer
        mappings={mappings}
        onToggleActive={handleToggleActive}
        onDelete={handleDeleteMapping}
        onDeleteComplete={() => queryClient.invalidateQueries({ queryKey: ['bot-mappings'] })}
        isUpdating={updateMappingMutation.isPending}
        isDeleting={deleteMappingMutation.isPending}
      />

      <BulkAddMappingDialog
        open={isBulkAddOpen}
        onOpenChange={setIsBulkAddOpen}
        bots={bots}
        onComplete={() => queryClient.invalidateQueries({ queryKey: ['bot-mappings'] })}
      />

      {/* Webhook URL Info */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ExternalLink className="h-5 w-5" />
            Webhook URL for Merged Signals
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Use this webhook URL to receive signals from child bots. Signals will only be forwarded if the mapping has specific endpoints configured.
            </p>
            <div className="bg-muted p-3 rounded-lg font-mono text-sm">
              https://lbvhywxvthwgiebjsrlr.supabase.co/functions/v1/bot-signal-merge-webhook
            </div>
            <div className="text-xs text-muted-foreground">
              <strong>Note:</strong> Send POST requests with the child bot's signal token. Signals will only be forwarded if the mapping has its own endpoints configured.
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
